import {AvatarStack, Box, IssueLabelToken, Octicon} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CircleIcon, CommentIcon, IssueClosedIcon, IssueOpenedIcon, PersonIcon} from '@primer/octicons-react'
import {ListItem, ListView} from '@github-ui/list-view'
import {SubIssuesActionBar} from './SubIssuesActionBar'
import {useLazyLoadQuery, type FetchPolicy, graphql} from 'react-relay'
import {useCallback, useEffect, useState} from 'react'
import type {SubIssuesListViewQuery} from './__generated__/SubIssuesListViewQuery.graphql'
import {SubIssuesSummary} from './SubIssuesSummary'

const SubIssuesListViewGraphQLQuery = graphql`
  query SubIssuesListViewQuery($issueID: ID!) {
    node(id: $issueID) {
      ... on Issue {
        id
        subIssues(first: 50) {
          nodes {
            id
            title
            titleHTML
            url
            state
            labels(first: 10) {
              nodes {
                id
                name
                color
              }
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            ...SubIssuesSummary
            totalCommentsCount
          }
        }
      }
    }
  }
`

export function SubIssuesListView({
  issueID,
  shouldRefetch,
  onRefetch,
}: {
  issueID: string
  /** Indicates whether the sub-issues list data should be refetched (e.g., if it is stale) */
  shouldRefetch: boolean
  /** Callback called when we've sent the request to refetch the sub-issues data */
  onRefetch: () => void
}) {
  // This looks a little bit unconvential, but this approach was taken from the Relay docs here:
  // https://relay.dev/docs/guided-tour/refetching/refreshing-queries/#when-using-uselazyloadquery
  // In the future, we may be able to replace this refetching with a refetchable fragment that exists
  // in one of the issue-viewer queries, rather than making a separate query and needing to refetch manually.
  const [fetchOptions, setFetchOptions] = useState<{fetchKey: number; fetchPolicy: FetchPolicy} | null>(null)

  const refetch = useCallback(() => {
    setFetchOptions(prevOptions => ({
      fetchKey: (prevOptions?.fetchKey ?? 0) + 1,
      fetchPolicy: 'store-and-network',
    }))
    onRefetch()
  }, [onRefetch])

  useEffect(() => {
    // Refetch the sub-issues data when indicated (e.g., data is stale because a sub-issue was added or removed)
    if (shouldRefetch) {
      refetch()
    }
  }, [refetch, shouldRefetch])

  const data = useLazyLoadQuery<SubIssuesListViewQuery>(SubIssuesListViewGraphQLQuery, {issueID}, fetchOptions ?? {})
  if (!data.node?.subIssues) return null

  return (
    <ListView
      variant="compact"
      title="Sub-issues"
      metadata={
        <ListView.Metadata
          density="condensed"
          title="Sub-issues"
          sx={{background: 'none', height: 40, borderBottom: 0}}
        />
      }
    >
      {data.node.subIssues.nodes?.map(issueNode => {
        if (!issueNode) return null
        return (
          <ListItem
            key={issueNode.id}
            title={
              <>
                <ListItem.Title href={issueNode.url} value={issueNode.titleHTML ?? issueNode.title} />
                <SubIssuesSummary issue={issueNode} />
              </>
            }
            secondaryActions={<SubIssuesActionBar issueId={issueID} subIssueId={issueNode.id} />}
            metadata={
              <ListItem.Metadata sx={{display: 'flex', gap: 2}}>
                {issueNode.labels?.nodes?.map(label => {
                  if (!label) return null
                  return <IssueLabelToken key={label.id} fillColor={`#${label.color}`} text={label.name} />
                })}
                <Box sx={{width: 36, display: 'flex', alignItems: 'center', gap: 1}}>
                  {issueNode.totalCommentsCount && issueNode.totalCommentsCount > 0 ? (
                    <>
                      <CommentIcon />
                      {issueNode.totalCommentsCount}
                    </>
                  ) : null}
                </Box>
                <Box sx={{width: 40, display: 'flex', justifyContent: 'flex-end'}}>
                  {issueNode.assignees.totalCount === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 100,
                        position: 'relative',
                        mr: '-2px',
                      }}
                    >
                      <Octicon icon={PersonIcon} sx={{position: 'absolute', color: 'border.muted'}} />
                      <Octicon size={24} icon={CircleIcon} sx={{color: 'border.muted'}} />
                    </Box>
                  ) : (
                    <AvatarStack alignRight>
                      {issueNode.assignees.edges?.map(assigneeEdge => {
                        if (!assigneeEdge?.node) return null
                        return (
                          <GitHubAvatar
                            key={assigneeEdge.node.id}
                            alt={assigneeEdge.node.login}
                            src={assigneeEdge.node.avatarUrl}
                          />
                        )
                      })}
                    </AvatarStack>
                  )}
                </Box>
              </ListItem.Metadata>
            }
          >
            <ListItem.LeadingContent>
              <ListItem.LeadingVisual
                icon={issueNode.state === 'CLOSED' ? IssueClosedIcon : IssueOpenedIcon}
                color={issueNode.state === 'CLOSED' ? 'done.fg' : 'open.fg'}
              />
            </ListItem.LeadingContent>
          </ListItem>
        )
      })}
    </ListView>
  )
}

try{ SubIssuesListView.displayName ||= 'SubIssuesListView' } catch {}